// src/pages/PainelCliente/MeusResgates.js
// Pedidos de resgate com as etapas de entrega. Mesma API de antes.

import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, Gift, Search } from "lucide-react";
import axios from "../../axiosConfig";
import { formatarPontos, getImagemUrl, lerUsuario } from "../../utils/tikpontos";
import "./MeusResgates.css";

const POR_PAGINA = 8;

const ETAPAS = [
  { valor: "pedido", nome: "Pedido" },
  { valor: "preparacao", nome: "Preparação" },
  { valor: "transporte", nome: "Transporte" },
  { valor: "enviado", nome: "Enviado" },
];

const STATUS = {
  pedido: { nome: "Pedido recebido", tom: "" },
  preparacao: { nome: "Em preparação", tom: "laranja" },
  transporte: { nome: "Em transporte", tom: "azul" },
  enviado: { nome: "Enviado", tom: "verde" },
  cancelado: { nome: "Cancelado", tom: "vermelho" },
  rejeitado: { nome: "Cancelado", tom: "vermelho" },
};

const formatarData = (v) => (v ? new Date(v).toLocaleDateString("pt-BR") : "");

function MeusResgates() {
  const usuario = useMemo(() => lerUsuario(), []);
  const [resgates, setResgates] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [historicoAberto, setHistoricoAberto] = useState(null);
  const [filtroNome, setFiltroNome] = useState("");
  const [filtroStatus, setFiltroStatus] = useState("todos");
  const [pagina, setPagina] = useState(1);

  useEffect(() => {
    if (!usuario?.id) return;
    axios
      .get(`/resgates/cliente/${usuario.id}`)
      .then((res) => setResgates(Array.isArray(res.data) ? res.data : []))
      .catch((err) => console.error("Erro ao carregar resgates:", err))
      .finally(() => setCarregando(false));
  }, [usuario?.id]);

  useEffect(() => setPagina(1), [filtroNome, filtroStatus]);

  const filtrados = resgates
    .filter((r) => !filtroNome || String(r.nome_premio || "").toLowerCase().includes(filtroNome.toLowerCase()))
    .filter(
      (r) =>
        filtroStatus === "todos" ||
        r.status === filtroStatus ||
        (filtroStatus === "rejeitado" && r.status === "cancelado")
    )
    .sort((a, b) => new Date(b.data_resgate) - new Date(a.data_resgate));

  const totalPaginas = Math.max(1, Math.ceil(filtrados.length / POR_PAGINA));
  const paginaLista = filtrados.slice((pagina - 1) * POR_PAGINA, pagina * POR_PAGINA);

  return (
    <div className="mr">
      <header className="tk-cabecalho-pagina">
        <div>
          <h1 className="tk-titulo-pagina">Meus resgates</h1>
          <p className="tk-subtitulo-pagina">Acompanhe a entrega de cada prêmio que você resgatou.</p>
        </div>
      </header>

      <section className="mr-filtros" data-tour="mr-filtros" aria-label="Filtrar pedidos">
        <label className="mr-busca">
          <span className="tk-sr-only">Buscar pelo nome do prêmio</span>
          <Search size={18} aria-hidden="true" />
          <input
            className="tk-input"
            type="search"
            placeholder="Buscar pelo nome do prêmio"
            value={filtroNome}
            onChange={(e) => setFiltroNome(e.target.value)}
          />
        </label>
        <label className="mr-status">
          <span className="tk-sr-only">Status</span>
          <select className="tk-select" value={filtroStatus} onChange={(e) => setFiltroStatus(e.target.value)}>
            <option value="todos">Todos os status</option>
            <option value="pedido">Pedido recebido</option>
            <option value="preparacao">Em preparação</option>
            <option value="transporte">Em transporte</option>
            <option value="enviado">Enviado</option>
            <option value="rejeitado">Cancelado</option>
          </select>
        </label>
      </section>

      {carregando ? (
        <div className="mr-lista">
          {[0, 1].map((i) => (
            <div key={i} className="tk-skeleton mr-skeleton" />
          ))}
        </div>
      ) : paginaLista.length === 0 ? (
        <div className="tk-vazio">
          {resgates.length === 0 ? (
            <>
              <strong>Você ainda não fez nenhum resgate</strong>
              <span>Escolha um prêmio no catálogo e acompanhe a entrega por aqui.</span>
              <Link to="/painel-cliente/catalogo-premios" className="tk-btn tk-btn--primario">
                Ver catálogo de prêmios
              </Link>
            </>
          ) : (
            <>
              <strong>Nenhum pedido com esses filtros</strong>
              <span>Mude o status ou o nome buscado.</span>
            </>
          )}
        </div>
      ) : (
        <div className="mr-lista">
          {paginaLista.map((r, i) => {
            const cancelado = r.status === "cancelado" || r.status === "rejeitado";
            const indice = ETAPAS.findIndex((e) => e.valor === r.status);
            const st = STATUS[r.status] || { nome: r.status, tom: "" };
            const porUnidade = Number(r.pontos_produto || 0) + Number(r.pontos_frete || 0);
            const totalPontos = r.pontos_total ?? Number(r.pontos_necessarios || 0) * Number(r.quantidade || 1);
            const chave = `${r.id}-${r.nome_premio}-${r.data_resgate}`;
            const aberto = historicoAberto === chave;

            return (
              <article key={chave} className="mr-card" data-tour={i === 0 ? "mr-card" : undefined}>
                <div className="mr-card-topo">
                  <span className="mr-foto">
                    {r.imagem_premio ? (
                      <img src={getImagemUrl(r.imagem_premio)} alt="" loading="lazy" />
                    ) : (
                      <Gift size={24} aria-hidden="true" />
                    )}
                  </span>
                  <div className="mr-info">
                    <div className="mr-titulo">
                      <h2>{r.nome_premio}</h2>
                      <span className={`tk-etiqueta ${st.tom ? `tk-etiqueta--${st.tom}` : ""}`}>{st.nome}</span>
                    </div>
                    <span className="mr-pedido">
                      Pedido #{r.id} · {formatarData(r.data_resgate)}
                    </span>
                    <dl className="mr-dados">
                      <div>
                        <dt>Quantidade</dt>
                        <dd>{r.quantidade}</dd>
                      </div>
                      <div>
                        <dt>Por unidade</dt>
                        <dd>{formatarPontos(porUnidade)} pts</dd>
                      </div>
                      <div>
                        <dt>Total</dt>
                        <dd>{formatarPontos(totalPontos)} pts</dd>
                      </div>
                    </dl>
                  </div>
                </div>

                {cancelado ? (
                  <p className="mr-cancelado">
                    Este pedido foi cancelado.{r.observacoes ? " Veja o motivo no histórico abaixo." : ""}
                  </p>
                ) : (
                  <ol className="mr-etapas" data-tour={i === 0 ? "mr-etapas" : undefined}>
                    {ETAPAS.map((etapa, idx) => {
                      const estado = idx < indice ? "is-feito" : idx === indice ? "is-atual" : "";
                      const data = r[`data_${etapa.valor}`];
                      return (
                        <li key={etapa.valor} className={estado} aria-current={idx === indice ? "step" : undefined}>
                          <span className="mr-etapa-bolinha" aria-hidden="true" />
                          <span className="mr-etapa-nome">{etapa.nome}</span>
                          <span className="mr-etapa-data">{formatarData(data)}</span>
                        </li>
                      );
                    })}
                  </ol>
                )}

                {r.observacoes && (
                  <div className="mr-historico">
                    <button
                      type="button"
                      className="mr-historico-btn"
                      aria-expanded={aberto}
                      onClick={() => setHistoricoAberto(aberto ? null : chave)}
                    >
                      {aberto ? "Ocultar histórico" : "Ver histórico"}
                      <ChevronDown size={16} className={aberto ? "is-aberto" : ""} aria-hidden="true" />
                    </button>
                    {aberto && (
                      <ul className="mr-historico-lista">
                        {String(r.observacoes)
                          .split("\n")
                          .filter(Boolean)
                          .map((linha, j) => (
                            <li key={j}>{linha}</li>
                          ))}
                      </ul>
                    )}
                  </div>
                )}
              </article>
            );
          })}
        </div>
      )}

      {totalPaginas > 1 && (
        <nav className="tk-paginacao" aria-label="Páginas de resgates">
          <button
            type="button"
            className="tk-btn tk-btn--secundario"
            onClick={() => setPagina((p) => Math.max(1, p - 1))}
            disabled={pagina === 1}
          >
            Anterior
          </button>
          <span>
            Página {pagina} de {totalPaginas}
          </span>
          <button
            type="button"
            className="tk-btn tk-btn--secundario"
            onClick={() => setPagina((p) => Math.min(totalPaginas, p + 1))}
            disabled={pagina === totalPaginas}
          >
            Próxima
          </button>
        </nav>
      )}
    </div>
  );
}

export default MeusResgates;
